import { useState, useEffect } from 'react'
import { referralsAPI } from '../services/api'
import { useDashboard } from '../contexts/DashboardContext'
import { formatDate } from '../utils/dateUtils'
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  TicketIcon,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

export default function ReferralApprovals() {
  const [referrals, setReferrals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedReferral, setSelectedReferral] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [filter, setFilter] = useState('all')
  const [approveNotes, setApproveNotes] = useState('')
  const [rejectReason, setRejectReason] = useState('')
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const { refreshStats } = useDashboard()

  useEffect(() => {
    loadReferrals()
  }, [filter])

  const loadReferrals = async () => {
    try {
      setLoading(true)
      const params = filter !== 'all' ? { status_filter: filter } : {}
      const response = await referralsAPI.getAll(params)
      setReferrals(response.data.referrals || [])
    } catch (err) {
      console.error('Failed to load referrals:', err)
      setError('Failed to load referral requests')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (referral) => {
    try {
      setActionLoading(true)
      await referralsAPI.approve(referral.id, approveNotes)
      await loadReferrals()
      refreshStats()
      setSelectedReferral(null)
      setShowApproveModal(false)
      setApproveNotes('')
    } catch (err) {
      console.error('Failed to approve referral:', err)
      const detail = err.response?.data?.detail
      alert('Failed to approve: ' + (typeof detail === 'string' ? detail : err.message))
    } finally {
      setActionLoading(false)
    }
  }

  const handleReject = async (referral) => {
    try {
      setActionLoading(true)
      await referralsAPI.reject(referral.id, rejectReason, '')
      await loadReferrals()
      refreshStats()
      setSelectedReferral(null)
      setShowRejectModal(false)
      setRejectReason('')
    } catch (err) {
      console.error('Failed to reject referral:', err)
      const detail = err.response?.data?.detail
      alert('Failed to reject: ' + (typeof detail === 'string' ? detail : err.message))
    } finally {
      setActionLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
    switch (status) {
      case 'approved':
        return <span className={`${baseClasses} bg-brand-50 text-brand-700`}>Approved</span>
      case 'rejected':
        return <span className={`${baseClasses} bg-red-50 text-red-700`}>Rejected</span>
      case 'redeemed':
        return <span className={`${baseClasses} bg-green-50 text-green-700`}>Redeemed</span>
      case 'expired':
        return <span className={`${baseClasses} bg-slate-100 text-slate-700`}>Expired</span>
      default:
        return <span className={`${baseClasses} bg-amber-50 text-amber-700`}>Pending</span>
    }
  }

  if (loading && !referrals.length) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Referral Approvals</h1>
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-brand-600" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Referral Approvals</h1>
          <p className="text-sm text-slate-500 mt-1">Manage referral code requests from personal email registrations</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border border-slate-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="all">All Referrals</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="redeemed">Redeemed</option>
          </select>
          <button
            onClick={loadReferrals}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* Referrals Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell">Website</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden sm:table-cell">Setup Mode</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">Requested</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {referrals.map((referral) => (
                <tr key={referral.id} className="hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => setSelectedReferral(referral)}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-slate-900">{referral.user_name || 'N/A'}</div>
                    <div className="text-xs text-slate-500">{referral.user_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-700">{referral.company || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                    {referral.company_website ? (
                      <a
                        href={referral.company_website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-brand-600 hover:text-brand-700 underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {referral.company_website.replace(/^https?:\/\//, '').slice(0, 30)}
                      </a>
                    ) : (
                      <span className="text-sm text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      referral.setup_mode === 'simple' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'
                    }`}>
                      {referral.setup_mode === 'simple' ? 'Simple' : 'Advanced'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                    <span className="text-sm font-mono text-slate-600">
                      {referral.code && referral.code !== 'PENDING' ? referral.code : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(referral.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 hidden lg:table-cell">
                    {formatDate(referral.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedReferral(referral) }}
                        className="text-brand-600 hover:text-brand-700 p-1"
                        title="View Details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      {referral.status === 'pending' && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedReferral(referral)
                              setShowApproveModal(true)
                            }}
                            disabled={actionLoading}
                            className="text-green-600 hover:text-green-700 px-2 py-1 text-xs font-medium"
                            title="Approve"
                          >
                            Approve
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedReferral(referral)
                              setShowRejectModal(true)
                            }}
                            disabled={actionLoading}
                            className="text-red-600 hover:text-red-700 px-2 py-1 text-xs font-medium"
                            title="Reject"
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {referrals.length === 0 && (
            <div className="text-center py-12">
              <TicketIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-sm text-slate-400">{filter === 'all' ? 'No referral requests yet' : `No ${filter} referral requests`}</p>
            </div>
          )}
        </div>
      </div>

      {/* Referral Detail Modal */}
      {selectedReferral && !showApproveModal && !showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-slate-900">Referral Details</h3>
                <button
                  onClick={() => setSelectedReferral(null)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Name</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedReferral.user_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Email</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedReferral.user_email}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Company</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedReferral.company || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Website</label>
                    <p className="mt-1 text-sm text-slate-900">
                      {selectedReferral.company_website ? (
                        <a href={selectedReferral.company_website} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-700 underline">
                          {selectedReferral.company_website}
                        </a>
                      ) : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Setup Mode</label>
                    <p className="mt-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedReferral.setup_mode === 'simple' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'
                      }`}>
                        {selectedReferral.setup_mode === 'simple' ? 'Simple Setup' : 'Advanced Setup'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Status</label>
                    <p className="mt-1">{getStatusBadge(selectedReferral.status)}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Referral Code</label>
                    <p className="mt-1 text-sm font-mono text-slate-900">
                      {selectedReferral.code && selectedReferral.code !== 'PENDING' ? selectedReferral.code : 'Not generated'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Requested</label>
                    <p className="mt-1 text-sm text-slate-900">{formatDate(selectedReferral.created_at)}</p>
                  </div>
                  {selectedReferral.approved_at && (
                    <div>
                      <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Approved</label>
                      <p className="mt-1 text-sm text-slate-900">{formatDate(selectedReferral.approved_at)}</p>
                    </div>
                  )}
                  {selectedReferral.redeemed_at && (
                    <div>
                      <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Redeemed</label>
                      <p className="mt-1 text-sm text-slate-900">{formatDate(selectedReferral.redeemed_at)}</p>
                    </div>
                  )}
                  {selectedReferral.expires_at && (
                    <div>
                      <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Expires</label>
                      <p className="mt-1 text-sm text-slate-900">{formatDate(selectedReferral.expires_at)}</p>
                    </div>
                  )}
                  {selectedReferral.admin_notes && (
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Admin Notes</label>
                      <p className="mt-1 text-sm text-slate-900">{selectedReferral.admin_notes}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setSelectedReferral(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Close
                </button>
                {selectedReferral.status === 'pending' && (
                  <>
                    <button
                      onClick={() => setShowApproveModal(true)}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                      disabled={actionLoading}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => setShowRejectModal(true)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                      disabled={actionLoading}
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedReferral && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Approve Referral Request</h3>
              <p className="text-sm text-slate-500 mb-4">
                Approving will generate a referral code and email it to <strong className="text-slate-700">{selectedReferral.user_email}</strong>.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Admin Notes (Optional)</label>
                <textarea
                  value={approveNotes}
                  onChange={(e) => setApproveNotes(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                  rows={3}
                  placeholder="Internal notes about this approval..."
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { setShowApproveModal(false); setApproveNotes('') }}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleApprove(selectedReferral)}
                  disabled={actionLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {actionLoading ? 'Approving...' : 'Approve & Send Code'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedReferral && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Reject Referral Request</h3>
              <p className="text-sm text-slate-500 mb-4">
                The user <strong className="text-slate-700">{selectedReferral.user_email}</strong> will be notified of the rejection.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Rejection Reason</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  rows={3}
                  placeholder="Reason for rejection (will be shared with user)..."
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { setShowRejectModal(false); setRejectReason('') }}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleReject(selectedReferral)}
                  disabled={actionLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {actionLoading ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
